import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import { Security } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService';
import { useAuthStore } from '../../store/authStore';
import { LoggerService } from '../../services/LoggerService';

const schema = yup.object({
  username: yup.string().required('Username is required'),
  password: yup.string().required('Password is required'),
});

type FormData = yup.InferType<typeof schema>;

interface DemoLoginModalProps {
  open: boolean;
  onClose: () => void;
}

const DemoLoginModal: React.FC<DemoLoginModalProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { login, setUser } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passwordValue, setPasswordValue] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      username: 'Demo',
      password: '', // Explicitly set password to empty - user MUST enter it
    },
    mode: 'onSubmit', // Only validate on submit, not on change
  });

  const onSubmit = async (data: FormData) => {
    // CRITICAL: Validate that password is provided and not empty
    if (!data.password || data.password.trim() === '') {
      setError('Password is required');
      enqueueSnackbar('Password is required', { variant: 'error' });
      return;
    }

    // CRITICAL: Validate username is provided
    if (!data.username || data.username.trim() === '') {
      setError('Username is required');
      enqueueSnackbar('Username is required', { variant: 'error' });
      return;
    }

    setIsLoading(true);
    setError(null);
    
    LoggerService.debug('[DemoLoginModal] Attempting login with credentials (password hidden)');
    
    try {
      const response = await authService.login(data);

      if (response.requires_2fa) {
        // Demo user shouldn't have 2FA, but handle just in case
        setError('Demo account has 2FA enabled. Please contact support.');
        setIsLoading(false);
        return;
      }

      // Get user info
      const user = await authService.getCurrentUser(response.access_token);

      // Store auth state
      login(
        response.access_token,
        response.refresh_token,
        response.family_id,
        response.expires_in,
        user
      );

      // Verify we have valid tokens before navigating
      if (!response.access_token) {
        throw new Error('No access token received from server');
      }

      // Close modal and navigate only after successful authentication
      reset();
      onClose();
      enqueueSnackbar('Demo login successful!', { variant: 'success' });
      
      // Small delay to ensure auth state is updated before navigation
      setTimeout(() => {
        navigate('/dashboard');
      }, 100);
    } catch (err: any) {
      LoggerService.error('Demo login failed:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Login failed. Please check your credentials.';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      reset();
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Prevent closing by clicking outside or pressing ESC
        // User must either login or click Cancel button
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
        handleClose();
      }}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Security sx={{ color: 'primary.main' }} />
          <Typography variant="h6">Try Demo</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Enter your demo credentials to access the demo environment.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box 
          component="form" 
          onSubmit={(e) => {
            e.preventDefault();
            // Prevent any auto-submission
            const formData = new FormData(e.currentTarget);
            const password = formData.get('password') as string;
            if (!password || password.trim() === '') {
              setError('Password is required');
              return;
            }
            handleSubmit(onSubmit)(e);
          }} 
          id="demo-login-form"
        >
          <TextField
            margin="normal"
            required
            fullWidth
            id="username"
            label="Username"
            autoComplete="username"
            autoFocus
            {...register('username', {
              required: 'Username is required',
              validate: (value) => value && value.trim() !== '' || 'Username cannot be empty'
            })}
            error={!!errors.username}
            helperText={errors.username?.message}
            disabled={isLoading}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            label="Password"
            type="password"
            id="password"
            autoComplete="current-password"
            {...register('password', { 
              required: 'Password is required',
              validate: (value) => value && value.trim() !== '' || 'Password cannot be empty'
            })}
            onChange={(e) => {
              setPasswordValue(e.target.value);
              register('password').onChange(e);
            }}
            error={!!errors.password}
            helperText={errors.password?.message}
            disabled={isLoading}
            inputProps={{
              minLength: 1,
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="demo-login-form"
          variant="contained"
          disabled={isLoading || !passwordValue || passwordValue.trim() === ''}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isLoading ? 'Logging in...' : 'Login'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DemoLoginModal;

